import { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * usePresence - Custom hook for tracking online presence
 * Part of PRD-020 Module 2: Online Presence Indicators
 *
 * Tracks user presence on CrewLink and displays "Online" badges
 * Uses Supabase Realtime Presence for real-time tracking
 */
export function usePresence(channelName = 'crewlink') {
    const { supabase, currentUser } = useAuth();
    const [onlineUsers, setOnlineUsers] = useState(new Set());
    const channelRef = useRef(null);

    useEffect(() => {
        if (!supabase || !currentUser) return;

        // Create a channel for presence tracking
        const channel = supabase.channel(channelName, {
            config: {
                presence: {
                    key: currentUser.id
                }
            }
        });

        // Track presence state changes
        channel
            .on('presence', { event: 'sync' }, () => {
                const state = channel.presenceState();
                const userIds = new Set();

                // Extract all online user IDs
                Object.keys(state).forEach(key => {
                    state[key].forEach(presence => {
                        if (presence.user_id) {
                            userIds.add(presence.user_id);
                        }
                    });
                });

                setOnlineUsers(userIds);
            })
            .on('presence', { event: 'join' }, ({ key, newPresences }) => {
                setOnlineUsers(prev => {
                    const updated = new Set(prev);
                    newPresences.forEach(presence => {
                        if (presence.user_id) {
                            updated.add(presence.user_id);
                        }
                    });
                    return updated;
                });
            })
            .on('presence', { event: 'leave' }, ({ key, leftPresences }) => {
                setOnlineUsers(prev => {
                    const updated = new Set(prev);
                    leftPresences.forEach(presence => {
                        if (presence.user_id) {
                            updated.delete(presence.user_id);
                        }
                    });
                    return updated;
                });
            })
            .subscribe(async (status) => {
                if (status === 'SUBSCRIBED') {
                    // Track own presence
                    await channel.track({
                        user_id: currentUser.id,
                        display_name: currentUser.display_name || 'Anonymous',
                        online_at: new Date().toISOString()
                    });
                }
            });

        channelRef.current = channel;

        // Cleanup on unmount
        return () => {
            if (channelRef.current) {
                channelRef.current.untrack();
                channelRef.current.unsubscribe();
            }
        };
    }, [supabase, currentUser, channelName]);

    // Helper function to check if a user is online
    const isUserOnline = (userId) => {
        return onlineUsers.has(userId);
    };

    return {
        onlineUsers,
        isUserOnline,
        onlineCount: onlineUsers.size
    };
}

/**
 * useUserActivity - Track last activity timestamp
 * Stores last seen timestamp in localStorage and database
 */
export function useUserActivity() {
    const { supabase, currentUser } = useAuth();
    const activityTimerRef = useRef(null);

    useEffect(() => {
        if (!supabase || !currentUser) return;

        // Update activity timestamp
        const updateActivity = async () => {
            try {
                const timestamp = new Date().toISOString();

                // Update in localStorage for immediate access
                localStorage.setItem(`last_activity_${currentUser.id}`, timestamp);

                // Update in database (optional - for persistent tracking)
                await supabase
                    .from('profiles')
                    .update({ last_activity_at: timestamp })
                    .eq('id', currentUser.id);
            } catch (err) {
                console.error('Error updating activity:', err);
            }
        };

        // Update immediately
        updateActivity();

        // Update every 5 minutes
        activityTimerRef.current = setInterval(updateActivity, 5 * 60 * 1000);

        // PERFORMANCE FIX: Debounce activity updates to prevent excessive database writes
        let debounceTimer = null;
        const DEBOUNCE_DELAY_MS = 5000; // 5 seconds

        const handleActivity = () => {
            // Clear existing timer
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }

            // Set new timer - only update after 5 seconds of inactivity
            debounceTimer = setTimeout(() => {
                updateActivity();
            }, DEBOUNCE_DELAY_MS);
        };

        window.addEventListener('focus', handleActivity);
        window.addEventListener('click', handleActivity);
        window.addEventListener('keypress', handleActivity);

        return () => {
            // Cleanup timers
            if (activityTimerRef.current) {
                clearInterval(activityTimerRef.current);
            }
            if (debounceTimer) {
                clearTimeout(debounceTimer);
            }
            // Cleanup event listeners
            window.removeEventListener('focus', handleActivity);
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('keypress', handleActivity);
        };
    }, [supabase, currentUser]);
}

/**
 * getLastActivity - Get user's last activity timestamp
 */
export function getLastActivity(userId) {
    const timestamp = localStorage.getItem(`last_activity_${userId}`);
    if (!timestamp) return null;

    const lastActivity = new Date(timestamp);
    const now = new Date();
    const diff = now - lastActivity;

    // Consider online if active within last 5 minutes
    if (diff < 5 * 60 * 1000) {
        return 'online';
    }

    // Format relative time
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
}
